import type { AssessedWord } from '../../../models/quiz';

/** The four bands pronunciation is graded with, the same as reading fluency in OLE. */
export type PronunciationBand = 'insufficient' | 'basic' | 'achieved' | 'excellent';

/** The four bands, plus `default` for a word or score without one. */
export type WordStyle = PronunciationBand | 'default';

export interface BandThresholds {
  basicFrom: number;
  achievedFrom: number;
  excellentFrom: number;
}

/** Fixed cuts, as OLE grades reading fluency, instead of the three bands Exercises sends. */
export const PRONUNCIATION_BANDS: Readonly<BandThresholds> = Object.freeze({
  basicFrom: 50,
  achievedFrom: 70,
  excellentFrom: 90,
});

export interface WordBreakdownPart {
  text: string;
  accuracy: number | null;
  style: WordStyle;
  resultLabel?: string;
}

export interface PronunciationWordDetail {
  text: string;
  accuracy: number | null;
  style: WordStyle;
  resultLabel?: string;
  omitted: boolean;
  syllables: WordBreakdownPart[];
  transcription?: string;
}

export const WORD_RESULT_LABELS: Record<WordStyle, string | undefined> = {
  insufficient: 'QUESTIONS.PRONUNCIATION.WORD_RESULT.INSUFFICIENT',
  basic: 'QUESTIONS.PRONUNCIATION.WORD_RESULT.BASIC',
  achieved: 'QUESTIONS.PRONUNCIATION.WORD_RESULT.ACHIEVED',
  excellent: 'QUESTIONS.PRONUNCIATION.WORD_RESULT.EXCELLENT',
  default: undefined,
};

const OMISSION_ERROR_TYPE = 'Omission';

/** The band of a 0-100 score, compared once rounded as it is shown, or `default` without score. */
export function toBand(
  score: number | null | undefined,
  { basicFrom, achievedFrom, excellentFrom }: BandThresholds = PRONUNCIATION_BANDS,
): WordStyle {
  if (typeof score !== 'number') return 'default';
  const rounded = Math.round(score);
  if (rounded >= excellentFrom) return 'excellent';
  if (rounded >= achievedFrom) return 'achieved';
  if (rounded >= basicFrom) return 'basic';
  return 'insufficient';
}

/** The band of a word by its accuracy; an omitted word without one is insufficient. */
export function toWordStyle(word: AssessedWord | undefined): WordStyle {
  if (word?.errorType === OMISSION_ERROR_TYPE && typeof word.accuracy !== 'number') {
    return 'insufficient';
  }
  return toBand(word?.accuracy);
}

/** The syllables to draw, or none when any of them has no letters to show. */
function toParts(units: { text: string | null; accuracy: number | null }[]): WordBreakdownPart[] {
  if (!units.length || units.some(({ text }) => !text?.trim())) return [];
  return units.map(({ text, accuracy }) => {
    const style = toBand(accuracy);
    return { text: text ?? '', accuracy, style, resultLabel: WORD_RESULT_LABELS[style] };
  });
}

/** The IPA with a dot between syllables, when Azure gave every syllable in IPA (en-US only). */
function toTranscription(word: AssessedWord | undefined): string | undefined {
  const syllables = word?.syllables ?? [];
  if (!syllables.length || syllables.some(({ syllable }) => !syllable?.trim())) return undefined;
  return `/${syllables.map(({ syllable }) => syllable).join('.')}/`;
}

/**
 * The written syllables whenever every one has its letters, which Exercises fills in every locale
 * it can, or the whole word as a single one so every language looks the same; plus the IPA
 * transcription when Azure gave it (en-US).
 */
export function buildWordDetail({
  word,
  speechText,
  style,
}: {
  word: AssessedWord | undefined;
  speechText: string;
  style: WordStyle;
}): PronunciationWordDetail {
  const syllables = toParts(
    (word?.syllables ?? []).map(({ grapheme, accuracy }) => ({ text: grapheme, accuracy })),
  );
  const accuracy = word?.accuracy ?? null;
  const resultLabel = WORD_RESULT_LABELS[style];
  return {
    text: speechText,
    accuracy,
    style,
    resultLabel,
    omitted: word?.errorType === OMISSION_ERROR_TYPE,
    syllables: syllables.length ? syllables : [{ text: speechText, accuracy, style, resultLabel }],
    transcription: syllables.length ? toTranscription(word) : undefined,
  };
}
